import React, { useCallback, type FC } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';

import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';

import { CustomizeIconComponent } from '../GlobalNavigationIcons.tsx';
import { i18n } from '../globalNavigationTranslations.ts';

import type { GlobalItemProps } from './globalItemProps.ts';
import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireCustomizeSidebarClickedAnalytics,
);

export const CustomizeSidebarItem: FC<GlobalItemProps> = ({
	isHidden,
	peekingId,
	setPeekingId,
	setIsCustomizeSidebarDialogVisible,
}) => {
	const routeDataRef = useRouteDataRef();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const onClick = useCallback(() => {
		setIsCustomizeSidebarDialogVisible(true);
		void fireClickAnalytics();
	}, [fireClickAnalytics, setIsCustomizeSidebarDialogVisible]);

	const shouldHide = useGlobalItemVisibility(
		'customize',
		false, // will never be "selected" if in the more menu
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<ButtonMenuItem elemBefore={CustomizeIconComponent} onClick={onClick}>
			<FormattedMessage {...i18n.customize} />
		</ButtonMenuItem>
	);
};
